/**
 * MobileBottomNav Component
 * Role-based bottom navigation for mobile devices.
 * 
 * Navigation per role (unified structure):
 * - Spelare: Hem, Kalender, Chatt, Mer
 * - Ledare: Hem, Kalender, Chatt, Mer (same as player)
 * - Förälder: Hem, Kalender, Chatt, Utveckling, Mer
 * 
 * Badge logic — "What do I need to do right now?"
 * 
 * PLAYER:
 *   - Evaluations to answer → badge
 *   - Unread chat → badge
 *   - (NO badge for: received praise, historical data)
 * 
 * COACH:
 *   - Evaluations to review → badge
 *   - Assessments (Bedömning) to complete → badge
 *   - Unread chat → badge
 *   - (NO badge for: sent evaluations, completed items, historical data)
 * 
 * Navigation:
 *   - Chat → unread messages count
 *   - Mer → total actionable items
 *   - Inside Mer → breakdown per item
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIOSViewport } from '../../hooks/useIOSViewport';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { isExerciseDetailRoute } from '../../lib/drillPresentation';
import { getTotalUnreadCount, getPendingEvaluationsCount, getPendingAssessmentsCount } from '../../lib/api';
import { useModules } from '../../context/ModulesContext';
import { useChatView } from '../../context/ChatViewContext';
import { usePresentationView } from '../../context/PresentationViewContext';
import { useWebSocketContext } from '../../context/WebSocketContext';
import {
  Home,
  Dumbbell,
  Trophy,
  MessageSquare,
  LayoutGrid,
  Calendar,
  Users,
  BarChart3,
  ClipboardList,
  FileVideo,
  Heart,
  Settings,
  LogOut,
  Star,
  Shield,
  HelpCircle,
  FileText,
  TrendingUp,
  User,
  Baby,
  Activity,
  Target,
  X,
  ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { Separator } from '../ui/separator';
import { useTranslation } from 'react-i18next';

// ============== ROLE-BASED NAVIGATION ==============
// `label` values are i18n keys resolved at render time via useTranslation.

// SPELARE - Bottom Nav (4): Hem, Kalender, Chatt, Mer
// Academy Home v1.0: Removed "Utveckling", simplified to 4 items
const playerNavItems = [
  { to: '/oversikt', icon: Home, label: 'navigation:items.home' },
  { to: '/calendar', icon: Calendar, label: 'navigation:items.calendar', moduleKey: 'calendar' },
  { to: '/chat', icon: MessageSquare, label: 'navigation:items.chat', moduleKey: 'chat' },
];

// LEDARE - Bottom Nav (4): Hem, Kalender, Chatt, Mer (matchar spelarvy)
const coachNavItems = [
  { to: '/oversikt', icon: Home, label: 'navigation:items.home' },
  { to: '/calendar', icon: Calendar, label: 'navigation:items.calendar', moduleKey: 'calendar' },
  { to: '/chat', icon: MessageSquare, label: 'navigation:items.chat', moduleKey: 'chat' },
];

// FÖRÄLDER - Bottom Nav (5): Hem, Kalender, Chatt, Utveckling, Mer
const parentNavItems = [
  { to: '/oversikt', icon: Home, label: 'navigation:items.home' },
  { to: '/calendar', icon: Calendar, label: 'navigation:items.calendar', moduleKey: 'calendar' },
  { to: '/chat', icon: MessageSquare, label: 'navigation:items.chat', moduleKey: 'chat' },
  { to: '/development', icon: TrendingUp, label: 'navigation:items.development_area', moduleKey: 'player_development' },
];

// ============== ROLE-BASED "MER" MENUS ==============

// SPELARE - Mer-meny (grouped: Lag & aktiviteter / Uppföljning / System)
const playerMoreSections = [
  {
    label: 'navigation:sections.team_activities',
    items: [
      { to: '/training', icon: Dumbbell, label: 'navigation:items.trainings', moduleKey: 'training' },
      { to: '/matches', icon: Trophy, label: 'navigation:items.matches', moduleKey: 'match' },
      { to: '/team', icon: Users, label: 'navigation:items.squad', moduleKey: 'squad' },
    ]
  },
  {
    label: 'navigation:sections.followup',
    items: [
      { to: '/development/evaluations', icon: ClipboardList, label: 'navigation:items.evaluations', moduleKey: 'evaluations' },
      { to: '/development/praise', icon: Heart, label: 'navigation:items.praise', moduleKey: 'praise' },
      { to: '/min-traning', icon: Target, label: 'navigation:items.my_training', moduleKey: 'individual_training' },
      { to: '/development/challenges', icon: Trophy, label: 'navigation:items.challenges', moduleKey: 'challenges' },
      { to: '/development/statistics', icon: BarChart3, label: 'navigation:items.statistics', moduleKey: 'statistics' },
    ]
  },
  {
    label: 'navigation:sections.system',
    items: [
      { to: '/media', icon: FileVideo, label: 'navigation:items.media', moduleKey: 'media' },
      { to: '/settings', icon: User, label: 'navigation:items.my_profile' },
      { to: '/feedback', icon: HelpCircle, label: 'navigation:items.support' },
    ]
  },
];

// LEDARE - Mer-meny (grouped: Lag & aktiviteter / Uppföljning / System)
const coachMoreSections = [
  {
    label: 'navigation:sections.team_activities',
    items: [
      { to: '/training', icon: Dumbbell, label: 'navigation:items.trainings', moduleKey: 'training' },
      { to: '/matches', icon: Trophy, label: 'navigation:items.matches', moduleKey: 'match' },
      { to: '/team', icon: Users, label: 'navigation:items.squad', moduleKey: 'squad' },
      { to: '/posts', icon: FileText, label: 'navigation:items.posts', moduleKey: 'posts' },
    ]
  },
  {
    label: 'navigation:sections.followup',
    items: [
      { to: '/development/evaluations', icon: ClipboardList, label: 'navigation:items.evaluations', moduleKey: 'evaluations' },
      { to: '/development/assessments', icon: Star, label: 'navigation:items.assessment', moduleKey: 'ratings' },
      { to: '/utveckling/individuell-traning', icon: Target, label: 'navigation:items.individual_training', moduleKey: 'individual_training' },
      { to: '/development/praise', icon: Heart, label: 'navigation:items.praise', moduleKey: 'praise' },
      { to: '/development/challenges', icon: Trophy, label: 'navigation:items.challenges', moduleKey: 'challenges' },
      { to: '/development/tests', icon: Activity, label: 'navigation:items.tests', moduleKey: 'tests' },
      { to: '/development/statistics', icon: BarChart3, label: 'navigation:items.statistics', moduleKey: 'statistics' },
    ]
  },
  {
    label: 'navigation:sections.system',
    items: [
      { to: '/media', icon: FileVideo, label: 'navigation:items.media', moduleKey: 'media' },
      { to: '/team/settings', icon: Settings, label: 'navigation:items.team_settings' },
      { to: '/settings', icon: User, label: 'navigation:items.my_profile' },
      { to: '/feedback', icon: HelpCircle, label: 'navigation:items.support' },
    ]
  },
];

// FÖRÄLDER - Mer-meny (förälder = passiv observatör, endast profil + support)
const parentMoreSections = [
  {
    label: 'navigation:sections.system',
    items: [
      { to: '/settings', icon: User, label: 'navigation:items.my_profile' },
      { to: '/feedback', icon: HelpCircle, label: 'navigation:items.support' },
    ]
  },
];

// ============== COMPONENTS ==============

// V25 R12 — Visual silence pass (FINAL). Lower contrast, quieter labels,
// virtually no blur. Apple Sports / Linear / Nike Training restraint.
// 🔒 NAVBAR LOCKED. No further tweaks past this iteration.
// Floating mobile navigation: warm selection, readable neutral labels.
const ACTIVE_TINT = '#E8C982';
const INACTIVE_TINT = '#B4B8C2';

const NavItem = ({ to, icon: Icon, label, isActive, unreadCount = 0 }) => {
  // Use the last segment of the URL as a stable, translation-independent test id.
  const testKey = (to || '').split('/').filter(Boolean).slice(-1)[0] || 'root';
  return (
  <NavLink
    to={to}
    className={cn(
      "flex flex-col items-center justify-center gap-0.5 h-[44px] px-1 relative z-10 min-w-0 rounded-full",
      "transition-colors duration-200 active:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#E8C982]"
    )}
    style={isActive ? { color: ACTIVE_TINT } : { color: INACTIVE_TINT }}
    data-testid={`mobile-nav-${testKey}`}
  >
    <div className="relative">
      <Icon
        style={{ width: 21, height: 21 }}
        strokeWidth={isActive ? 1.8 : 1.7}
      />
      {unreadCount > 0 && (
        <span
          className="absolute -top-1 -right-1.5 flex items-center justify-center text-[8px] font-semibold text-[rgb(var(--tn-text-ffffff))] rounded-full"
          style={{
            width: 13, height: 13,
            backgroundColor: '#D86A5A',
            boxShadow: '0 0 0 2px #0B0D14',
          }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </div>
    <span
      className="text-[10.5px] leading-tight truncate max-w-full"
      style={{ fontWeight: isActive ? 500 : 400 }}
    >
      {label}
    </span>
  </NavLink>
  );
};

const MoreMenuItem = ({ to, icon: Icon, label, onClick, badge }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  // Stable test-id independent of translated label.
  const testKey = (to || '').split('/').filter(Boolean).slice(-1)[0] || 'root';

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 min-h-[52px] transition-colors active:bg-[rgb(var(--tn-tint-ffffff))]/[0.04]",
        isActive
          ? "bg-amber-500/[0.08] text-[rgb(var(--tn-text-fde68a))]"
          : "text-[rgb(var(--tn-text-ffffff))]/85 hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.03]"
      )}
      data-testid={`more-menu-${testKey}`}
    >
      <span
        className={cn(
          "h-8 w-8 rounded-xl flex items-center justify-center shrink-0",
          isActive ? "bg-amber-500/15 text-[rgb(var(--tn-text-fcd34d))]" : "bg-[rgb(var(--tn-tint-ffffff))]/[0.04] text-[rgb(var(--tn-text-ffffff))]/70"
        )}
      >
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </span>
      <span className="text-[14px] font-medium flex-1 truncate">{label}</span>
      {badge > 0 && (
        <span
          className="min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center text-[rgb(var(--tn-text-ffffff))]"
          style={{ backgroundColor: '#D86A5A' }}
          data-testid={`badge-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      <ChevronRightIcon className="w-4 h-4 text-[rgb(var(--tn-text-ffffff))]/30 shrink-0" />
    </NavLink>
  );
};

const SectionHeader = ({ label }) => (
  <div className="px-2 pt-2 pb-1.5">
    <span className="text-[10px] font-semibold text-[rgb(var(--tn-text-ffffff))]/55 uppercase tracking-[0.12em]">
      {label}
    </span>
  </div>
);

export function MobileBottomNav() {
  const { isInChatView } = useChatView();
  // Only use viewport measurements to detect the keyboard. A cached numeric
  // height can survive iOS suspension; it must never position the navigation.
  // The body portal and CSS bottom anchor also apply immediately after resume.
  const viewport = useIOSViewport(!isInChatView, true);
  const { t } = useTranslation(['navigation']);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [pendingEvaluationsCount, setPendingEvaluationsCount] = useState(0);
  const [pendingAssessmentsCount, setPendingAssessmentsCount] = useState(0);
  const { logout, isCoach, isPlayer, isParent, user, activeTeam } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isPresentationOpen } = usePresentationView();
  const { registerHandler } = useWebSocketContext();
  
  // Get module context (with safe fallback)
  const modulesContext = useModules();
  const { isModuleEnabled } = modulesContext || {
    isModuleEnabled: () => true,
  };
  
  // Disabled modules disappear from the product surface for every role.
  // Team admins re-enable them centrally in Laginställningar → Funktioner.
  const filterByModule = (items) => {
    return items.filter(item => !item.moduleKey || isModuleEnabled(item.moduleKey));
  };
  
  // Determine which nav items and menu sections to use based on role
  const getNavConfig = () => {
    let baseNavItems, baseMoreSections;
    
    if (isCoach) {
      baseNavItems = coachNavItems;
      baseMoreSections = coachMoreSections;
    } else if (isParent) {
      baseNavItems = parentNavItems;
      baseMoreSections = parentMoreSections;
    } else {
      // Default to player
      baseNavItems = playerNavItems;
      baseMoreSections = playerMoreSections;
    }
    
    // Filter nav items by module status
    const filteredNavItems = filterByModule(baseNavItems);
    
    // Filter more menu sections by module status
    const filteredMoreSections = baseMoreSections.map(section => ({
      ...section,
      items: filterByModule(section.items)
    })).filter(section => section.items.length > 0);
    
    return { navItems: filteredNavItems, moreSections: filteredMoreSections };
  };
  
  const { navItems, moreSections } = getNavConfig();
  
  // Fetch actionable counts (role-aware)
  // Badges answer: "What do I need to do right now?"
  useEffect(() => {
    const fetchActionableCounts = async () => {
      try {
        const teamId = activeTeam?.id;
        const promises = [
          getTotalUnreadCount().catch(() => ({ data: { total: 0 } })),
          teamId
            ? getPendingEvaluationsCount(teamId).catch(() => ({ data: { count: 0 } }))
            : Promise.resolve({ data: { count: 0 } })
        ];
        // Coaches: also fetch pending assessments (Bedömning)
        if (isCoach && teamId) {
          promises.push(getPendingAssessmentsCount(teamId).catch(() => ({ data: { count: 0 } })));
        }
        const results = await Promise.all(promises);
        setUnreadChatCount(results[0].data?.total || 0);
        setPendingEvaluationsCount(results[1].data?.count || 0);
        setPendingAssessmentsCount(isCoach ? (results[2]?.data?.count || 0) : 0);
      } catch (e) {
        // Silently fail
      }
    };
    
    if (user) {
      fetchActionableCounts();
      const interval = setInterval(fetchActionableCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isCoach, activeTeam?.id]);

  // Real-time: increment unread badge when WebSocket delivers a new message
  useEffect(() => {
    if (!user) return;
    const unregister = registerHandler('mobile-bottom-nav-badge', {
      onMessage: (_channelId, message) => {
        // Only count messages from other users AND when not currently viewing chat
        if (message?.sender_user_id !== user.id && location.pathname !== '/chat') {
          setUnreadChatCount(prev => prev + 1);
        }
      }
    });
    return unregister;
  }, [user, registerHandler, location.pathname]);

  // Reset counts when visiting pages. Chat is intentionally excluded — the chat
  // badge must reflect real backend unread state, not a local fake 0.
  useEffect(() => {
    if (location.pathname === '/evaluations') {
      const timeout = setTimeout(() => setPendingEvaluationsCount(0), 1000);
      return () => clearTimeout(timeout);
    }
    if (location.pathname === '/assessments') {
      const timeout = setTimeout(() => setPendingAssessmentsCount(0), 1000);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);
  
  // HIDE bottom nav when in active chat view (for native app feel)
  // This must be AFTER all hooks to comply with React hooks rules
  if (isInChatView) {
    return null;
  }

  // HIDE bottom nav on the exercise-detail presentation page so it can never
  // overlap the 2.5D animation control dock (the page has its own Back action).
  // Route-based (deep links) OR overlay-state-based (mobile Sheet / inline open).
  if (isExerciseDetailRoute(location.pathname) || isPresentationOpen) {
    return null;
  }

  const handleLogout = () => {
    setMoreOpen(false);
    logout();
    navigate('/login');
  };

  // Check if current path matches nav item
  const isPathActive = (to) => {
    const basePath = to.split('?')[0];
    // All "home" paths (legacy + new) treat as home
    if (basePath === '/oversikt') {
      return location.pathname === '/' ||
             location.pathname === '/oversikt' ||
             location.pathname === '/dashboard' ||
             location.pathname === '/home' ||
             location.pathname === '/home/player' ||
             location.pathname === '/home/coach' ||
             location.pathname === '/lagstatus';
    }
    return location.pathname === basePath || location.pathname.startsWith(basePath + '/');
  };

  // Total badge for "Mer" button = total actionable items inside
  // Coach: evaluations + assessments
  // Player: evaluations only
  // Parent: 0 (förälder = passiv observatör, inga åtgärder)
  const merTotalBadge = isCoach
    ? (pendingEvaluationsCount + pendingAssessmentsCount)
    : isParent
    ? 0
    : pendingEvaluationsCount;

  // Badge count for specific routes inside "Mer" menu
  // Only actionable items get badges
  const getBadge = (to) => {
    if (to === '/evaluations' || to === '/development/evaluations') return pendingEvaluationsCount;
    if (to === '/assessments' || to === '/development/assessments') return isCoach ? pendingAssessmentsCount : 0;
    return 0;
  };

  // Get unread count for nav items
  const getNavUnread = (to) => {
    if (to === '/chat') return unreadChatCount;
    return 0;
  };

  // One shared indicator remains mounted as the active route changes.
  // This also adapts to parent navigation and disabled modules.
  const routeIndex = navItems.findIndex(item => isPathActive(item.to));
  const moreActive = moreOpen || routeIndex < 0;
  const activeIndex = moreActive ? navItems.length : routeIndex;
  const itemCount = navItems.length + 1;

  // V13 (Feb 2026): render the bottom-nav (and its associated More-menu Sheet)
  // into `document.body` via a portal. Otherwise an ancestor with `transform`,
  // `filter`, `backdrop-filter`, `will-change` or `contain` will become the
  // containing block of `position: fixed` and the nav will scroll with the
  // page on iOS Safari/PWA. The portal guarantees the nav is always anchored
  // to the viewport.
  return createPortal(
    <>
      <style>{`
        .trainnova-nav-indicator {
          transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .trainnova-nav-indicator { transition: none; }
        }
      `}</style>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40"
        style={{
          height: 'calc(66px + env(safe-area-inset-bottom, 0px))',
          bottom: 0,
          visibility: viewport.isKeyboardOpen ? 'hidden' : undefined,
          background: "linear-gradient(to bottom, rgb(var(--tn-bg-0e1016) / 0), rgb(var(--tn-bg-0e1016) / 0.48) 38%, rgb(var(--tn-bg-0e1016) / 0.9))",
        }}
      />
      <nav
        className="fixed z-40 mx-auto rounded-full"
        aria-label={t('navigation:items.more') === 'Mer' ? 'Huvudnavigation' : 'Main navigation'}
        style={{
          left: 'max(22px, env(safe-area-inset-left, 0px))',
          right: 'max(22px, env(safe-area-inset-right, 0px))',
          bottom: 'max(4px, env(safe-area-inset-bottom, 0px))',
          visibility: viewport.isKeyboardOpen ? 'hidden' : undefined,
          maxWidth: '460px',
          padding: '4px',
          background: "rgb(var(--tn-bg-11141b) / 0.78)",
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: "1px solid rgb(var(--tn-border-ffffff) / 0.045)",
          boxShadow: '0 3px 12px rgba(0,0,0,0.16)',
        }}
        data-testid="mobile-bottom-nav"
      >
        <div className="relative grid items-center" style={{ gridTemplateColumns: `repeat(${itemCount}, minmax(0, 1fr))` }}>
          <div
            aria-hidden="true"
            className="trainnova-nav-indicator pointer-events-none absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${100 / itemCount}%`,
              transform: `translateX(${activeIndex * 100}%)`,
              background: 'rgba(196,163,92,0.11)',
              boxShadow: 'inset 0 0 0 1px rgba(196,163,92,0.05)',
            }}
          />
          {navItems.map((item) => (
            <NavItem 
              key={item.to} 
              {...item} 
              label={t(item.label)}
              isActive={!moreActive && isPathActive(item.to)}
              unreadCount={getNavUnread(item.to)}
            />
          ))}
          
          {/* More button — mirrors NavItem styling exactly. */}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 h-[44px] px-1 transition-colors duration-200 active:opacity-70 min-w-0 relative z-10 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#E8C982]"
            style={moreActive ? { color: ACTIVE_TINT } : { color: INACTIVE_TINT }}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            data-testid="mobile-nav-more"
          >
            <div className="relative">
              <LayoutGrid style={{ width: 21, height: 21 }} strokeWidth={moreActive ? 1.8 : 1.7} />
              {merTotalBadge > 0 && (
                <span
                  className="absolute -top-1 -right-1.5 flex items-center justify-center text-[8px] font-semibold text-[rgb(var(--tn-text-ffffff))] rounded-full"
                  style={{
                    width: 13, height: 13,
                    backgroundColor: '#D86A5A',
                    boxShadow: '0 0 0 2px #0B0D14',
                  }}
                >
                  {merTotalBadge > 9 ? '9+' : merTotalBadge}
                </span>
              )}
            </div>
            <span
              className="text-[10.5px] leading-tight"
              style={{ fontWeight: moreActive ? 500 : 400 }}
            >
              {t('navigation:items.more')}
            </span>
          </button>
        </div>
      </nav>

      {/* More Menu Sheet */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent 
          side="bottom" 
          className="rounded-t-2xl bg-card border-t border-border p-0 overflow-hidden [&>button]:hidden"
          style={{
            maxHeight: '85dvh',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <SheetHeader className="px-4 pt-2 pb-1 bg-card shrink-0 space-y-0">
            <div aria-hidden="true" className="w-9 h-1 bg-[rgb(var(--tn-tint-ffffff))]/15 rounded-full mx-auto mb-1" />
            <div className="flex items-center justify-between gap-3">
              <SheetTitle className="text-left text-xl font-bold text-[rgb(var(--tn-text-ffffff))]">{t('navigation:items.more')}</SheetTitle>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label={t('navigation:actions.close', { defaultValue: 'Stäng' })}
                className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-full text-[rgb(var(--tn-text-ffffff))]/65 hover:text-[rgb(var(--tn-text-ffffff))] hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="mobile-more-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </SheetHeader>

          <nav className="px-3 pb-4 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {moreSections.map((section) => (
              <div key={section.label} className="mb-2">
                <SectionHeader label={t(section.label)} />
                <div className="rounded-2xl overflow-hidden border border-[rgb(var(--tn-border-ffffff))]/[0.05] bg-[rgb(var(--tn-tint-ffffff))]/[0.025] divide-y divide-[rgb(var(--tn-border-ffffff))]/[0.04]">
                  {section.items.map((item) => (
                    <MoreMenuItem
                      key={item.to}
                      {...item}
                      label={t(item.label)}
                      onClick={() => setMoreOpen(false)}
                      badge={getBadge(item.to)}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Logga ut — separate, distinct red treatment */}
            <div className="mt-2 mb-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[52px] rounded-2xl border border-red-500/20 bg-red-500/[0.06] text-[rgb(var(--tn-text-fca5a5))] hover:bg-red-500/[0.10] transition-colors"
                data-testid="mobile-logout-btn"
              >
                <span className="h-8 w-8 rounded-xl flex items-center justify-center bg-red-500/15 text-[rgb(var(--tn-text-fca5a5))] shrink-0">
                  <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
                </span>
                <span className="text-[14px] font-semibold flex-1 text-left">{t('navigation:actions.logout')}</span>
              </button>
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </>,
    document.body
  );
}

export default MobileBottomNav;
